import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Grid2,
  Container,
  Typography,
  Paper,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import handleAxiosError from "../../components/ErrorHandler";
import { notifErr, notifOk } from "../../components/Functions";
import {
  TrainingSchedule,
  trainingScheduleInit,
} from "../../model/entity/TrainingSchedule";
import {
  trainingScheduleValidationObjInit,
  validateTrainingSchedule,
} from "../../model/validation/TrainingScheduleValidation";
import { postFetcher } from "../../components/Fetchers";

const NewSchedule = () => {
  const navigate = useNavigate();
  const [triedSubmit, setTriedSubmit] = useState(false);
  const [schedule, setSchedule] =
    useState<TrainingSchedule>(trainingScheduleInit);
  const [scheduleErr, setScheduleErr] = useState(
    trainingScheduleValidationObjInit
  );

  useEffect(() => {
    if (triedSubmit) setScheduleErr(validateTrainingSchedule(schedule));
  }, [schedule, triedSubmit]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSchedule({ ...schedule, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTriedSubmit(true);

    const scheduleErrors = validateTrainingSchedule(schedule);
    setScheduleErr(scheduleErrors);

    if (Object.values(scheduleErrors).some((val) => val === false)) return;

    try {
      await postFetcher(`${process.env.REACT_APP_API_URL}/schedules`, schedule);
      notifOk("Rozvrh úspěšně vytvořen.");
      navigate("/schedule/settings");
    } catch (err) {
      notifErr(handleAxiosError(err));
    }
  };

  return (
    <Container
      maxWidth="xs"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pb: "2rem",
      }}
    >
      <Paper elevation={3} sx={{ padding: "1.5rem" }}>
        <Typography variant="h5" sx={{ marginBottom: "1.5rem" }}>
          Nový rozvrh
        </Typography>
        <Grid2 container spacing={3} size={{ xs: 12, md: 6 }}>
          <Grid2 size={{ xs: 12 }}>
            <TextField
              fullWidth
              size="small"
              label="Název"
              required
              name="name"
              value={schedule.name}
              error={!scheduleErr.name}
              onChange={handleInputChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="DD.MM.YYYY"
                label="Datum zahájení"
                sx={{ width: "100%" }}
                name="startDate"
                value={
                  schedule.startDate === "" ? null : dayjs(schedule.startDate)
                }
                onChange={(date) =>
                  setSchedule({
                    ...schedule,
                    startDate: date ? date.format("YYYY-MM-DD") : "",
                  })
                }
                slotProps={{
                  textField: {
                    size: "small",
                    required: true,
                    error:
                      !scheduleErr.startDate &&
                      !dayjs(schedule.startDate, "YYYY-MM-DD", true).isValid(),
                  },
                  field: { clearable: true },
                }}
              />
            </LocalizationProvider>
          </Grid2>
          <Grid2 size={{ xs: 12 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="DD.MM.YYYY"
                label="Datum ukončení"
                sx={{ width: "100%" }}
                name="endDate"
                value={schedule.endDate === "" ? null : dayjs(schedule.endDate)}
                onChange={(date) =>
                  setSchedule({
                    ...schedule,
                    endDate: date ? date.format("YYYY-MM-DD") : "",
                  })
                }
                slotProps={{
                  textField: {
                    size: "small",
                    required: true,
                    error:
                      !scheduleErr.endDate &&
                      !dayjs(schedule.endDate, "YYYY-MM-DD", true).isValid(),
                  },
                  field: { clearable: true },
                }}
              />
            </LocalizationProvider>
          </Grid2>

          <Grid2
            container
            size={{ xs: 12 }}
            sx={{ justifyContent: "right", gap: 2, mt: 1 }}
          >
            <Grid2>
              <Button
                className="cancelBtn"
                variant="outlined"
                onClick={() => navigate("/schedule/settings")}
              >
                Zrušit
              </Button>
            </Grid2>
            <Grid2>
              <Button
                className="submitBtn"
                variant="outlined"
                onClick={handleSubmit}
              >
                Vytvořit
              </Button>
            </Grid2>
          </Grid2>
        </Grid2>
      </Paper>
    </Container>
  );
};

export default NewSchedule;
